import { motion } from 'motion/react';
import { createContext, useContext } from 'react';

import type { ReactNode} from 'react';

import './NavStack.scss';

export interface NavStackHandler<T = string> {
	currentViewId: T;
	history: T[];
}

export interface NavStackProps<T = string> {
	handler: NavStackHandler<T>;
	children?: ReactNode[];
}

type NavStackContextValue<T = string> = {
	currentViewId: T;
};
const NavStackContext = createContext<NavStackContextValue<unknown> | null>(null);

/**
 * NavStack component to manage navigation between different views.
 * @param props - NavStackProps
 * @returns - JSX.Element
 */
export function NavStack<T = string>(props: NavStackProps<T>) {
	return (
		<NavStackContext.Provider value={{ currentViewId: props.handler.currentViewId }}>
			<div className='nav-stack'>{props.children}</div>
		</NavStackContext.Provider>
	);
}

export interface NavStackViewProps<T = string> {
	viewId: T;
	children?: ReactNode;
}

/**
 * NavStack.View component to define individual views within the NavStack.
 * @param props - NavStackViewProps
 * @returns - JSX.Element or null
 */
export function NavStackView<T = string>({ viewId, children }: NavStackViewProps<T>) {
	const ctx = useContext(NavStackContext);

	if (!ctx) {
		throw new Error('NavStack.View must be used inside NavStack');
	}

	const isActive = ctx.currentViewId === viewId;

	const variants = {
		active: {
			opacity: 1,
			x: 0,
			zIndex: 1,
			visibility: 'visible',
			transition: { duration: 0.25, ease: 'easeOut' },
		},
		inactive: {
			opacity: 0,
			x: -24,
			zIndex: 0,
			transition: { duration: 0.2, ease: 'easeIn' },
			transitionEnd: {
				visibility: 'hidden',
			},
		},
	} as const;

	return (
		<motion.div
			className='nav-stack-view'
			data-active-view={isActive}
			inert={isActive ? undefined : true}
			variants={variants}
			animate={isActive ? 'active' : 'inactive'}
			initial={false}>
			{children}
		</motion.div>
	);
}

NavStack.View = NavStackView;
